import { type ReactNode, useEffect, useLayoutEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useCreateModifyActions } from 'stores/useCreateModifyStore';
import {
  useCanAccessMultipleRooftops,
  useCurrentUser,
  useImpersonationError,
  useIsWhiteLabelScoped,
  useLastLogin,
  useUserActions,
  useUserFeatureFlags,
  useUserNotifications,
} from 'stores/useUserStore';

import { CreateModifyContext, type CreateModifyContextValue } from 'contexts/CreateModifyContext';
import { useNotification } from 'contexts/NotificationContext';
import { useGlobalDialog } from 'hooks/contexts/useGlobalDialog';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useRouter } from 'hooks/useRouter';
import { translate } from 'utils/intlUtils';

const { t } = translate;

export interface Props {
  children: ReactNode;
}

export const CreateModifyProvider = ({ children }: Props) => {
  const user = useCurrentUser();
  const userNotifications = useUserNotifications();
  const lastLogin = useLastLogin();
  const impersonationError = useImpersonationError();
  const userFeatureFlags = useUserFeatureFlags();
  const canAccessMultipleRooftops = useCanAccessMultipleRooftops();
  const isWhiteLabelScoped = useIsWhiteLabelScoped();
  const actions = useUserActions();
  const globalDialogContext = useGlobalDialog();
  const { showNotification } = useNotification();
  const routerContext = useRouter();
  const parameters = useParams();
  const { flags: featureFlags, updateFeatureFlagContext } = useFeatureFlags();

  const userContext = useMemo(
    () => ({
      canAccessMultipleRooftops,
      clearImpersonationError: actions.clearImpersonationError,
      featureFlags: userFeatureFlags,
      hasMinimumScope: actions.hasMinimumScope,
      hasPermissions: actions.hasPermissions,
      impersonateAsUser: actions.impersonateAsUser,
      impersonationError,
      isWhiteLabelScoped,
      lastLogin,
      setLastLogin: actions.setLastLogin,
      setUser: actions.setUser,
      setUserNotifications: actions.setUserNotifications,
      updateUserNotifications: actions.updateUserNotifications,
      user,
      userNotifications,
    }),
    [
      actions,
      canAccessMultipleRooftops,
      impersonationError,
      isWhiteLabelScoped,
      lastLogin,
      user,
      userFeatureFlags,
      userNotifications,
    ]
  );

  const { getField, getTier, setFeatureFlags, setOnBuilderLoadFailure, setTier, toggleTier } = useCreateModifyActions();

  useLayoutEffect(() => {
    setFeatureFlags(featureFlags);
  }, [setFeatureFlags, featureFlags]);

  useEffect(() => {
    setOnBuilderLoadFailure(() =>
      showNotification({
        children: t('connection_error_placeholder_message'),
        groupKey: 'builder-load-failed',
        variant: 'error',
      })
    );
  }, [setOnBuilderLoadFailure, showNotification]);

  const subContexts = useMemo(
    () => ({
      featureFlags,
      globalDialogContext,
      routerContext: {
        ...routerContext,
        params: parameters,
      },
      updateFeatureFlagContext,
      userContext,
    }),
    [featureFlags, globalDialogContext, parameters, routerContext, updateFeatureFlagContext, userContext]
  );

  const value = useMemo<CreateModifyContextValue>(
    () => ({ getField, getTier, setTier, subContexts, toggleTier }),
    [getField, getTier, setTier, subContexts, toggleTier]
  );

  return <CreateModifyContext value={value}>{children}</CreateModifyContext>;
};
